import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import { fetchRulesLotterySelect } from '~/utils/playLottery.js'
import { ID_KENO_FR } from '~/utils/constants/idsLotteries'

/**
 * Builds the API query-params object from the result-store state (country, lottery, numbers, etc.).
 * Returns `null` if no country is selected.
 * @param store - Subset of the result store with `country`, `lottery`, `numbers`, `isMutipleBets`, `amount`.
 */
export const getParams = (store: any) => {
	const { lottery, country, isMutipleBets, amount } = store
	let params: { [key: string]: any } = {}

	if (country === null) return null

	params.country_id = country.id

	if (lottery !== null) {
		params.lottery_id = lottery.id
	}
	// ✅ IMPORTANTES: mantenerlos en la URL
	// Usa snake_case para que coincida con tu query real
	if (typeof isMutipleBets !== 'undefined' && isMutipleBets !== null) {
		params.is_multiple_bets = String(isMutipleBets)
	}

	if (typeof amount !== 'undefined' && amount !== null) {
		params.amount = String(amount)
	}

	const numbers = getNumbers(store)
	if (numbers !== null) {
		params = { ...params, ...numbers }
	}

	return params
}

const getNumbers = (store: any) => {
	const { lottery, numbers, isMutipleBets } = store
	if (!lottery) return null

	const rulesLottery = getRulesLottery(lottery)
	const isClassicLottery = IDS_LOTTERIES_CLASSIC.includes(lottery.id)

	if (isClassicLottery) {
		const arr = sanitizeNumbers(numbers.first, rulesLottery.first_block, !!isMutipleBets)
		const [first_number = null, second_number = null, third_number = null, fourth_number = null, fifth_number = null] =
			arr
		return { fifth_number, first_number, fourth_number, second_number, third_number }
	}

	// ✅ Si es multiple bets, NO recortar
	const conditionLotteryKenoFR = lottery.id === ID_KENO_FR ? true : !!isMutipleBets
	const regular_balls = sanitizeNumbers(numbers?.first ?? [], rulesLottery.first_block, conditionLotteryKenoFR)

	if (!regular_balls.some((b) => b !== null)) return null

	if (numbers.last === null) {
		return { regular_balls, extra_balls: null }
	}

	const extra_balls = sanitizeNumbers(numbers.last, rulesLottery.last_block, !!isMutipleBets)
	return { regular_balls, extra_balls }
}

const getRulesLottery = (lottery: any) => fetchRulesLotterySelect(lottery)

const sanitizeNumbers = (numbers: any, rule: any, isMultipleBets = false): (number | null)[] => {
	const validNumbers = numbers
		.map((numStr: any) => {
			if (numStr === null || numStr === undefined) return null
			const num = Number(numStr)
			return !isNaN(num) && num >= rule.min && num <= rule.max ? num : null
		})
		.filter((num: any): num is number => num !== null)

	if (isMultipleBets) return validNumbers // ✅ NO recortar

	while (validNumbers.length < rule.total) validNumbers.push(null)
	return validNumbers.slice(0, rule.total)
}

/**
 * Returns `true` when all numbers in the store satisfy the lottery's min/max/count rules.
 * Delegates to the classic or regular lottery validator depending on the lottery type.
 * @param store - Object with `lottery` and `numbers` properties.
 */
export const validateAllNumbers = (store: any) => {
	const { lottery, numbers } = store

	const rulesLottery = getRulesLottery(lottery)
	const isClassicLottery = IDS_LOTTERIES_CLASSIC.includes(lottery.id)
	if (isClassicLottery) {
		return validateClassicLottery(numbers, rulesLottery)
	}
	return lottery.id === ID_KENO_FR
		? validateRegularLotteryKeno(numbers, rulesLottery)
		: validateRegularLottery(numbers, rulesLottery)
}

const validateClassicLottery = (numbers: any, rules: any) => {
	const { first } = numbers
	const { first_block } = rules
	if (first.length !== first_block.total) return false

	return validateNumbers(first, first_block)
}

const validateNumbers = (numbers: any, rule: any): boolean => {
	return numbers.every((numStr: any) => {
		const num = Number(numStr)
		return !isNaN(num) && num >= rule.min && num <= rule.max
	})
}

const validateRegularLottery = (numbers: any, rules: any) => {
	const { first, last } = numbers
	const { first_block, last_block } = rules
	if (first.length !== first_block.total) return false
	if (last === null) {
		return validateNumbers(first, first_block)
	}
	if (last.length !== last_block.total) return false

	return validateNumbers(first, first_block) && validateNumbers(last, last_block)
}

const validateRegularLotteryKeno = (numbers: any, rules: any) => {
	const { first, last } = numbers
	const { first_block } = rules
	if (first.length < first_block.min_select) return false
	if (last === null) {
		return validateNumbers(first, first_block)
	}
}
